import { useState, useMemo, useEffect } from "react";
import { Search, Plus, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { authFetch } from "@/lib/authFetch";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-2 — CategoryEditPopover (T11).
//
// Self-contained: recebe `trigger` (componente da pill), `tx` e
// `categoriesUsed`. Maneja open state interno + apply via PUT optimistic.
// onPatch é chamado 2x (otimista + final ou rollback).

export default function CategoryEditPopover({
  trigger,
  tx,
  categoriesUsed,
  onPatch,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  const options = useMemo(() => {
    const arr = (categoriesUsed || []).map(c => ({
      value: c.category, icon: c.icon, count: c.count,
    }));
    if (query) {
      const q = query.toLowerCase();
      const filtered = arr.filter(o => o.value.toLowerCase().includes(q));
      const exact = filtered.some(o => o.value.toLowerCase() === q);
      if (!exact && query.trim()) {
        return [...filtered, { value: query.trim(), icon: "✨", isNew: true }];
      }
      return filtered;
    }
    return arr;
  }, [categoriesUsed, query]);

  const apply = async (newCategory) => {
    if (!tx || !newCategory || newCategory === tx.category || saving) {
      setOpen(false);
      return;
    }
    setSaving(true);
    const previousCategory = tx.category;
    onPatch?.(tx.id, { category: newCategory });
    try {
      const res = await authFetch(`${API_URL}/api/finance/transactions/${tx.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category: newCategory }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const fresh = await res.json();
      onPatch?.(tx.id, { category: fresh.category });
      toast.success(`Categoria atualizada: ${fresh.category}`);
    } catch (e) {
      onPatch?.(tx.id, { category: previousCategory });
      toast.error("Não foi possível salvar. Tente novamente.");
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-72 p-0 bg-surface-overlay border border-subtle"
        data-testid="extrato-category-edit-popover"
      >
        <div className="px-3 pt-3 pb-2 border-b border-subtle">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-foreground-muted pointer-events-none" />
            <Input
              type="search"
              inputMode="search"
              autoFocus
              placeholder="Buscar ou criar..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-8 h-8 text-caption"
              aria-label="Buscar categoria"
              data-testid="extrato-category-edit-search"
              onKeyDown={(e) => {
                if (e.key === "Enter" && options.length > 0) {
                  apply(options[0].value);
                }
              }}
            />
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto py-1">
          {options.length === 0 ? (
            <div className="px-3 py-4 text-caption text-foreground-muted text-center">
              Digite pra criar uma categoria nova
            </div>
          ) : (
            options.map(opt => {
              const isCurrent = opt.value === tx.category;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => apply(opt.value)}
                  disabled={saving}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3 py-2 text-caption transition-colors text-left",
                    isCurrent
                      ? "bg-brand-cyan/10 text-foreground"
                      : "text-foreground-muted hover:bg-surface-card-hover hover:text-foreground",
                    saving && "opacity-50",
                  )}
                  data-testid={`extrato-category-edit-option-${opt.value}`}
                >
                  <span className="text-base leading-none" aria-hidden="true">{opt.icon || "📦"}</span>
                  <span className="flex-1 truncate">{opt.value}</span>
                  {opt.isNew && (
                    <span className="inline-flex items-center gap-0.5 text-micro text-status-warning">
                      <Plus className="w-3 h-3" /> nova
                    </span>
                  )}
                  {isCurrent && !opt.isNew && (
                    <Check className="w-3.5 h-3.5 text-brand-cyan" aria-hidden="true" />
                  )}
                  {!opt.isNew && typeof opt.count === "number" && !isCurrent && (
                    <span className="text-micro text-foreground-subtle tabular-nums">{opt.count}</span>
                  )}
                </button>
              );
            })
          )}
        </div>

        {saving && (
          <div className="px-3 py-2 border-t border-subtle text-micro text-foreground-muted inline-flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" /> salvando...
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
